import { TimingPacket, TimingReason, TimingScope, TIMING_VERSION } from '../types/learningTiming';
export function timingId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const h = Array.from(bytes, n => n.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
/** Injected monotonic clock; discard uncertain >2s sample gaps, not reading inactivity. */
export class ForegroundClock {
  private last: number;
  private start: number;
  private end: number;
  private sequence = 0;
  private active = false;
  private closed = false;
  constructor(private scope: TimingScope, private revision: string | null, private timelineId: string,
    public visitId: string, private now: () => number, private emit: (p: TimingPacket) => void,
    private unsupported = false) {
    this.last = this.start = this.end = Math.floor(now());
    this.packet('open', unsupported ? 'unsupported' : 'checkpoint');
  }
  private packet(kind: TimingPacket['kind'], reason: TimingReason, lossDetected = false) {
    this.emit({timingVersion: TIMING_VERSION, visitId: this.visitId, timelineId: this.timelineId,
      sequence: this.sequence++, kind, scope: this.scope, contentRevision: this.revision,
      startOffsetMs: this.start, endOffsetMs: this.end, elapsedMs: this.end - this.start,
      reason: this.unsupported ? 'unsupported' : reason, lossDetected, coordination: 'unverified'});
    this.start = this.end;
  }
  sample() {
    if (this.closed) return;
    const now = Math.floor(this.now()), gap = now - this.last;
    if (this.active && !this.unsupported) {
      if (gap < 0 || gap > 2000) {
        this.flush('suspension');
        this.start = this.end = now;
        this.packet('interval', 'suspension', true);
      } else {
        this.end = now;
        while (this.end - this.start >= 15000) {
          this.end = this.start + 15000;
          this.flush('checkpoint');
          this.end = now;
        }
      }
    } else this.start = this.end = now;
    this.last = now;
  }
  setActive(active: boolean, reason: TimingReason = 'pause') {
    if (this.closed) return;
    this.sample();
    if (this.active && !active) this.flush(reason);
    this.active = active;
    if (active && this.end === this.start) this.start = this.end = Math.floor(this.now());
  }
  flush(reason: TimingReason) {
    if (this.end > this.start) this.packet('interval', reason);
  }
  checkpoint(reason: TimingReason) { this.sample(); this.flush(reason); }
  markCoverage(reason: TimingReason) {
    if (!this.closed) this.packet('interval', reason, true);
  }
  close(reason: TimingReason = 'navigation') {
    if (this.closed) return;
    this.setActive(false, reason);
    this.packet('close', reason, reason === 'pagehide');
    this.closed = true;
  }
}
